import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  listKnowledgePacks,
  deleteKnowledgePack,
  ACTIVE_PACK_TYPES,
  PACK_STATUS,
  getPackTypeIcon,
  getPackStatusColor,
  formatDateTime,
} from '../api/knowledgePacks';
import LanguageToggle from '../components/LanguageToggle';
import { useLang } from '../contexts/LanguageContext';

/**
 * NEXUS V6.3 Phase 2B - Knowledge Pack Manager Dashboard
 * Grid/List view with filters for Admin/Management
 * RBAC: Admin (full), Management (CRUD), Supervisor (read), Executor (no access)
 */
const KnowledgePackManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useLang();

  // State
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    search: '',
  });

  // RBAC Check
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management' || user?.role === 'management_functional';
  const isSupervisor = user?.role === 'supervisor';
  const isExecutor = user?.role === 'executor';

  const canCreate = isAdmin || isManagement;
  const canEdit = isAdmin || isManagement;
  const canDelete = isAdmin || isManagement;
  const canRead = isAdmin || isManagement || isSupervisor;

  // Redirect executor (no access)
  useEffect(() => {
    if (isExecutor) {
      navigate('/dashboard');
    }
  }, [isExecutor, navigate]);

  // Fetch packs
  const fetchPacks = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        user_role: user?.role || 'executor',
        limit: 50,
        skip: 0,
      };

      if (filters.type !== 'all') {
        params.type = filters.type;
      }
      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.search.trim()) {
        params.search = filters.search.trim();
      }

      const response = await listKnowledgePacks(params);
      setPacks(response.packs || []);
    } catch (err) {
      console.error('Failed to fetch packs:', err);
      // Handle error properly - err.message might be an object
      const errorMsg = typeof err.message === 'string' ? err.message : 
                       typeof err === 'string' ? err :
                       JSON.stringify(err.message || err);
      setError(errorMsg || 'Failed to load knowledge packs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) {
      fetchPacks();
    }
  }, [filters, canRead]);

  // Handle delete
  const handleDelete = async (packId) => {
    if (!canDelete) {
      alert(t('knowledgePacks.noPermission'));
      return;
    }

    const confirmDelete = window.confirm(t('knowledgePacks.confirmArchive'));

    if (!confirmDelete) return;

    try {
      await deleteKnowledgePack(packId, user?.user_id, user?.role);
      alert(t('knowledgePacks.archiveSuccess'));
      fetchPacks(); // Refresh list
    } catch (err) {
      console.error('Failed to delete pack:', err);
      alert(`${t('knowledgePacks.archiveFailed')}: ${err.message}`);
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Navigate to wizard (create new)
  const handleCreateNew = () => {
    navigate('/knowledge-packs/wizard');
  };

  // Navigate to edit
  const handleEdit = (packId) => {
    navigate(`/knowledge-packs/wizard/${packId}`);
  };

  // Navigate to version history
  const handleViewVersions = (packId) => {
    navigate(`/knowledge-packs/${packId}/versions`);
  };

  // Navigate to audit logs
  const handleViewAudit = (packId) => {
    navigate(`/knowledge-packs/${packId}/audit`);
  };

  // No access for executor
  if (isExecutor) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="text-white px-4 py-3 shadow-md" style={{ background: '#0e67a2' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-lg">
              📦
            </div>
            <div>
              <h1 className="text-lg font-black leading-tight">{t('knowledgePacks.title')}</h1>
              <p className="text-xs font-medium opacity-90">{t('knowledgePacks.subtitle')}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {/* Language Toggle - Compact */}
            <LanguageToggle className="mb-0.5" />
            
            {(isAdmin || isManagement) && (
              <span className="text-[10px] font-medium px-2 py-0.5 bg-white/20 rounded">
                🟢 {t('knowledgePacks.isoCompliance')}
              </span>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="text-xs font-semibold px-3 py-1 bg-white/10 hover:bg-white/20 rounded transition"
            >
              ← {t('common.back')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 pb-20 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 64px)' }}>
        {/* Filters & Controls */}
        <div className="bg-white rounded-xl shadow-sm p-3 mb-3">
          {/* Search */}
          <div className="mb-3">
            <input
              type="text"
              placeholder={`🔍 ${t('knowledgePacks.searchPlaceholder')}`}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter Row */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {/* Type Filter */}
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('knowledgePacks.allTypes')}</option>
              {ACTIVE_PACK_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('knowledgePacks.allStatus')}</option>
              <option value="draft">{t('common.draft')}</option>
              <option value="published">{t('common.active')}</option>
              <option value="archived">{t('common.archived')}</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('knowledgePacks.gridView')}
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('knowledgePacks.listView')}
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {canCreate && (
              <button
                onClick={handleCreateNew}
                className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-bold rounded-lg shadow-md hover:from-orange-600 hover:to-orange-700 transition"
              >
                + {t('knowledgePacks.createPack')}
              </button>
            )}
            <button
              onClick={fetchPacks}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              🔄
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-white rounded-lg shadow-sm p-3 text-center">
            <div className="text-2xl font-black text-blue-600">{packs.length}</div>
            <div className="text-xs font-medium text-gray-600">{t('knowledgePacks.totalPacks')}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 text-center">
            <div className="text-2xl font-black text-green-600">
              {packs.filter(p => p.status === 'published').length}
            </div>
            <div className="text-xs font-medium text-gray-600">{t('knowledgePacks.activePacks')}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 text-center">
            <div className="text-2xl font-black text-gray-500">
              {packs.filter(p => p.status === 'draft').length}
            </div>
            <div className="text-xs font-medium text-gray-600">{t('knowledgePacks.draftPacks')}</div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-4xl mb-2">⏳</div>
            <p className="text-sm font-medium text-gray-600">{t('knowledgePacks.loadingPacks')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3">
            <p className="text-sm font-semibold text-red-600">⚠️ {error}</p>
            <button
              onClick={fetchPacks}
              className="mt-2 text-xs font-semibold text-red-600 underline"
            >
              {t('knowledgePacks.tryAgain')}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && packs.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-5xl mb-3">📦</div>
            <p className="text-base font-bold text-gray-800 mb-1">{t('knowledgePacks.noPacksFound')}</p>
            <p className="text-xs text-gray-600 mb-4">
              {filters.search || filters.type !== 'all' || filters.status !== 'all'
                ? t('knowledgePacks.adjustFilters')
                : t('knowledgePacks.createFirstPack')}
            </p>
            {canCreate && (
              <button
                onClick={handleCreateNew}
                className="px-6 py-2.5 bg-orange-500 text-white text-sm font-bold rounded-lg shadow-md hover:bg-orange-600 transition"
              >
                + {t('knowledgePacks.createFirstPackButton')}
              </button>
            )}
          </div>
        )}

        {/* Grid View */}
        {!loading && !error && packs.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 gap-3">
            {packs.map(pack => (
              <PackCardGrid
                key={pack.pack_id}
                pack={pack}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => handleEdit(pack.pack_id)}
                onDelete={() => handleDelete(pack.pack_id)}
                onViewVersions={() => handleViewVersions(pack.pack_id)}
                onViewAudit={() => handleViewAudit(pack.pack_id)}
              />
            ))}
          </div>
        )}

        {/* List View */}
        {!loading && !error && packs.length > 0 && viewMode === 'list' && (
          <div className="space-y-2">
            {packs.map(pack => (
              <PackCardList
                key={pack.pack_id}
                pack={pack}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => handleEdit(pack.pack_id)}
                onDelete={() => handleDelete(pack.pack_id)}
                onViewVersions={() => handleViewVersions(pack.pack_id)}
                onViewAudit={() => handleViewAudit(pack.pack_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Pack Card - Grid View
// ============================================================================
const PackCardGrid = ({ pack, canEdit, canDelete, onEdit, onDelete, onViewVersions, onViewAudit }) => {
  const [showActions, setShowActions] = useState(false);
  const t = useLang();

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200 hover:shadow-md transition">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="text-2xl">{getPackTypeIcon(pack.type)}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-black text-gray-900 truncate">
              {typeof pack.name === 'string' ? pack.name : pack.name?.en || pack.name?.ms || 'Untitled Pack'}
            </h3>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${getPackStatusColor(pack.status)}`}>
                {typeof pack.status === 'string' ? pack.status.toUpperCase() : 'UNKNOWN'}
              </span>
              <span className="text-[10px] font-medium text-gray-500">v{pack.version || 1}</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowActions(!showActions)}
          className="text-gray-500 hover:text-gray-700 text-lg font-bold"
        >
          ⋮
        </button>
      </div>

      {/* Content Preview */}
      <div className="space-y-2 mb-3">
        <div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-0.5">{t('knowledgePacks.hardConstraints')}</p>
          <p className="text-xs text-gray-700 line-clamp-2">
            {pack.hard_constraints?.slice(0, 2).map(c => {
              // Handle different formats: string, {en, ms}, {value}
              if (typeof c === 'string') return c;
              if (c?.en) return c.en;
              if (c?.ms) return c.ms;
              if (c?.value) return c.value;
              return 'N/A';
            }).join(' • ') || t('knowledgePacks.noConstraints')}
          </p>
        </div>
        {pack.tags && pack.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {pack.tags.slice(0, 3).map((tag, idx) => {
              // Handle different tag formats
              const tagText = typeof tag === 'string' ? tag : (tag?.en || tag?.ms || tag?.value || 'tag');
              return (
                <span key={`item-${idx}`} className="text-[10px] font-medium px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                  #{tagText}
                </span>
              );
            })}
            {pack.tags.length > 3 && (
              <span className="text-[10px] font-medium text-gray-500">+{pack.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <span className="text-[10px] text-gray-500">
          {t('knowledgePacks.updated')} {formatDateTime(pack.updated_at)}
        </span>
        <span className="text-xs font-semibold text-blue-600">{pack.type}</span>
      </div>

      {/* Action Menu */}
      {showActions && (
        <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-2">
          {canEdit && pack.status !== 'archived' && (
            <button
              onClick={onEdit}
              className="py-2 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
            >
              ✏️ {t('common.edit')}
            </button>
          )}
          <button
            onClick={onViewVersions}
            className="py-2 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            📜 {t('knowledgePacks.versionHistory')}
          </button>
          <button
            onClick={onViewAudit}
            className="py-2 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            📋 {t('knowledgePacks.activityLog')}
          </button>
          {canDelete && pack.status !== 'archived' && (
            <button
              onClick={onDelete}
              className="py-2 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
            >
              🗑️ {t('knowledgePacks.archive')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Pack Card - List View
// ============================================================================
const PackCardList = ({ pack, canEdit, canDelete, onEdit, onDelete, onViewVersions, onViewAudit }) => {
  const t = useLang();
  return (
    <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        {/* Left: Icon + Name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="text-xl">{getPackTypeIcon(pack.type)}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 truncate">
              {typeof pack.name === 'string' ? pack.name : pack.name?.en || pack.name?.ms || 'Untitled Pack'}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getPackStatusColor(pack.status)}`}>
                {typeof pack.status === 'string' ? pack.status : 'unknown'}
              </span>
              <span className="text-[10px] text-gray-500">v{pack.version || 1}</span>
              <span className="text-[10px] text-gray-500">{typeof pack.type === 'string' ? pack.type : 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          {canEdit && pack.status !== 'archived' && (
            <button
              onClick={onEdit}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
              title={t('common.edit')}
            >
              ✏️
            </button>
          )}
          <button
            onClick={onViewVersions}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded transition"
            title={t('knowledgePacks.versionHistory')}
          >
            📜
          </button>
          <button
            onClick={onViewAudit}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded transition"
            title={t('knowledgePacks.activityLog')}
          >
            📋
          </button>
          {canDelete && pack.status !== 'archived' && (
            <button
              onClick={onDelete}
              className="p-2 text-red-600 hover:bg-red-50 rounded transition"
              title={t('knowledgePacks.archive')}
            >
              🗑️
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgePackManager;
